import { supabase } from '../lib/supabase'
import type { AdmCadastroInput, AdmCategoria, AdmCentroCusto } from '../types/admCadastros'

function sanitizeAiExamples(aiExamples: string[]): string[] {
  const seen = new Set<string>()
  const sanitized: string[] = []

  for (const rawValue of aiExamples) {
    const value = rawValue.trim()
    if (!value) continue

    const key = value.toLocaleLowerCase('pt-BR')
    if (seen.has(key)) continue

    seen.add(key)
    sanitized.push(value)
  }

  return sanitized
}

export const admCadastrosService = {
  // ── Categorias ─────────────────────────────────────────────────────────────

  async listCategorias(): Promise<AdmCategoria[]> {
    const { data, error } = await supabase
      .from('adm_categories')
      .select('*')
      .is('deleted_at', null)
      .order('name')

    if (error) throw error
    return (data ?? []) as AdmCategoria[]
  },

  async upsertCategoria(input: AdmCadastroInput): Promise<void> {
    const aiExamples = sanitizeAiExamples(input.ai_examples)
    const payload = {
      name: input.name.trim(),
      description: input.description.trim() || null,
      ai_examples: aiExamples.length > 0 ? aiExamples : null,
    }

    if (input.id) {
      const { error } = await supabase
        .from('adm_categories')
        .update(payload)
        .eq('id', input.id)
      if (error) throw error
    } else {
      const { error } = await supabase
        .from('adm_categories')
        .insert(payload)
      if (error) throw error
    }
  },

  async softDeleteCategoria(id: string, reason: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('adm_categories')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: userId,
        deleted_reason: reason,
      })
      .eq('id', id)

    if (error) throw error
  },

  // ── Centros de Custo ────────────────────────────────────────────────────────

  async listCentrosCusto(): Promise<AdmCentroCusto[]> {
    const { data, error } = await supabase
      .from('adm_cost_centers')
      .select('*')
      .is('deleted_at', null)
      .order('name')

    if (error) throw error
    return (data ?? []) as AdmCentroCusto[]
  },

  async upsertCentroCusto(input: AdmCadastroInput): Promise<void> {
    const aiExamples = sanitizeAiExamples(input.ai_examples)
    const payload = {
      name: input.name.trim(),
      description: input.description.trim() || null,
      ai_examples: aiExamples.length > 0 ? aiExamples : null,
    }

    if (input.id) {
      const { error } = await supabase
        .from('adm_cost_centers')
        .update(payload)
        .eq('id', input.id)
      if (error) throw error
    } else {
      const { error } = await supabase
        .from('adm_cost_centers')
        .insert(payload)
      if (error) throw error
    }
  },

  async softDeleteCentroCusto(id: string, reason: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('adm_cost_centers')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: userId,
        deleted_reason: reason,
      })
      .eq('id', id)

    if (error) throw error
  },
}
